import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Plus,
    Trash2,
    Play,
    CheckCircle2,
    Receipt as ReceiptIcon,
    FileText,
} from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import CrewPicker from "@/components/CrewPicker";
import {
    StatusBadge,
    money,
    fmtDate,
    fmtDateTime,
    EXPENSE_CATEGORIES,
    JOB_STATUSES,
} from "@/lib/format";

export default function JobDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [notes, setNotes] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get(`/jobs/${id}`);
            setJob(data.job);
            setNotes(data.job.notes || "");
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    const setStatus = async (status) => {
        setBusy(true);
        try {
            await api.put(`/jobs/${id}`, { status });
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const saveNotes = async () => {
        setBusy(true);
        try {
            await api.put(`/jobs/${id}`, { notes });
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const generateInvoice = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/invoices`, { job_id: id, due_in_days: 14 });
            navigate(`/invoices/${data.invoice.id}`);
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    if (!job) {
        return (
            <div className="page-shell">
                {err ? (
                    <div
                        className="brand-card"
                        style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    >
                        {err}
                    </div>
                ) : (
                    <p className="text-muted">Loading job…</p>
                )}
            </div>
        );
    }

    const canStart = job.status === "scheduled";
    const canComplete = job.status === "in_progress";
    const canInvoice = job.status === "completed" && !job.invoice_id;

    return (
        <div className="page-shell">
            <Link
                to="/jobs"
                className="link-red inline-flex items-center gap-1 text-sm"
                data-testid="jd-back"
            >
                <ArrowLeft size={14} />
                Back to jobs
            </Link>

            <header className="fade-up mt-4 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <p className="eyebrow">Job</p>
                    <h1 className="h1 mt-3" data-testid="jd-number">
                        {job.job_number}
                    </h1>
                    <div className="flex items-center gap-3 mt-2">
                        <StatusBadge status={job.status} testId="jd-status" />
                        <span className="text-muted text-sm">
                            From {job.estimate_number} · Created {fmtDate(job.created_at)}
                        </span>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {canStart && (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setStatus("in_progress")}
                            disabled={busy}
                            data-testid="jd-start"
                        >
                            <Play size={14} />
                            Mark in progress
                        </button>
                    )}
                    {canComplete && (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setStatus("completed")}
                            disabled={busy}
                            data-testid="jd-complete"
                        >
                            <CheckCircle2 size={14} />
                            Mark complete
                        </button>
                    )}
                    {canInvoice && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={generateInvoice}
                            disabled={busy}
                            data-testid="jd-invoice"
                        >
                            <ReceiptIcon size={14} />
                            Generate invoice
                        </button>
                    )}
                    {job.invoice_id && (
                        <Link
                            to={`/invoices/${job.invoice_id}`}
                            className="btn btn-primary"
                            data-testid="jd-open-invoice"
                        >
                            <ReceiptIcon size={14} />
                            Open invoice
                        </Link>
                    )}
                    <Link
                        to={`/estimates/${job.estimate_id}`}
                        className="btn btn-ghost"
                        data-testid="jd-open-estimate"
                    >
                        <FileText size={14} />
                        Open estimate
                    </Link>
                </div>
            </header>

            {err && (
                <div
                    className="brand-card mt-4"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="jd-error"
                >
                    {err}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
                <div className="lg:col-span-2 flex flex-col gap-5">
                    <div className="brand-card fade-up">
                        <p className="eyebrow">Client &amp; site</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                            <div>
                                <p className="text-xs text-muted">Client</p>
                                <p className="font-display font-bold">{job.client?.name}</p>
                                <p className="text-sm text-muted">
                                    {job.client?.email} · {job.client?.phone || ""}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-muted">Job address</p>
                                <p>{job.job_address}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted">Service</p>
                                <p className="capitalize">{job.service_type}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted">Estimate total</p>
                                <p className="font-mono font-bold">
                                    {money(job.estimate_grand_total)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <ExpenseSection job={job} reload={load} />

                    <ChangeOrderSection job={job} reload={load} />
                </div>

                <div className="flex flex-col gap-5">
                    <div className="brand-card fade-up" data-testid="jd-schedule">
                        <p className="eyebrow">Schedule</p>
                        <ScheduleEditor job={job} reload={load} />
                    </div>

                    <div className="brand-card fade-up delay-1" data-testid="jd-crew">
                        <CrewPicker
                            jobId={job.id}
                            selected={job.crew}
                            start={job.scheduled_start}
                            end={job.scheduled_end}
                            onSaved={load}
                        />
                    </div>

                    <div className="brand-card fade-up delay-1">
                        <p className="eyebrow">Status</p>
                        <select
                            className="field-select mt-3"
                            value={job.status}
                            onChange={(e) => setStatus(e.target.value)}
                            data-testid="jd-status-select"
                        >
                            {JOB_STATUSES.map((s) => (
                                <option key={s} value={s} className="capitalize">
                                    {s.replace("_", " ")}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="brand-card fade-up delay-2">
                        <p className="eyebrow">Notes</p>
                        <textarea
                            rows={5}
                            className="field-textarea mt-3"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="jd-notes"
                        />
                        <button
                            type="button"
                            className="btn btn-secondary mt-3 w-full"
                            disabled={busy}
                            onClick={saveNotes}
                            data-testid="jd-save-notes"
                        >
                            Save notes
                        </button>
                    </div>

                    <div className="brand-card fade-up delay-3" data-testid="jd-summary">
                        <p className="eyebrow">Job summary</p>
                        <ul className="mt-3 text-sm space-y-1">
                            <li className="flex justify-between">
                                <span className="text-muted">Estimate total</span>
                                <span className="font-mono">
                                    {money(job.estimate_grand_total)}
                                </span>
                            </li>
                            <li className="flex justify-between">
                                <span className="text-muted">Expenses</span>
                                <span className="font-mono">{money(job.total_expenses)}</span>
                            </li>
                            <li className="flex justify-between">
                                <span className="text-muted">Approved change orders</span>
                                <span className="font-mono">
                                    {money(job.change_orders_total)}
                                </span>
                            </li>
                            <li
                                className="flex justify-between pt-2 mt-1 border-t border-[var(--brand-surface)] font-display font-bold"
                                style={{ fontSize: "1.05rem" }}
                            >
                                <span>Projected revenue</span>
                                <span
                                    className="font-mono"
                                    style={{ color: "var(--brand-red)" }}
                                >
                                    {money(
                                        (job.estimate_grand_total || 0) +
                                            (job.change_orders_total || 0),
                                    )}
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ScheduleEditor({ job, reload }) {
    const [start, setStart] = useState(job.scheduled_start ? job.scheduled_start.slice(0, 16) : "");
    const [end, setEnd] = useState(job.scheduled_end ? job.scheduled_end.slice(0, 16) : "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/jobs/${job.id}`, {
                scheduled_start: start ? new Date(start).toISOString() : null,
                scheduled_end: end ? new Date(end).toISOString() : null,
            });
            await reload();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-3 flex flex-col gap-3">
            <div>
                <label className="field-label">Scheduled start</label>
                <input
                    type="datetime-local"
                    className="field-input"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    data-testid="jd-sched-start"
                />
            </div>
            <div>
                <label className="field-label">Scheduled end</label>
                <input
                    type="datetime-local"
                    className="field-input"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                    data-testid="jd-sched-end"
                />
            </div>
            <button
                type="button"
                className="btn btn-secondary"
                disabled={saving}
                onClick={save}
                data-testid="jd-sched-save"
            >
                Save schedule
            </button>
            {job.actual_start && (
                <p className="text-xs text-muted">
                    Started {fmtDateTime(job.actual_start)}
                    {job.actual_end ? ` · finished ${fmtDateTime(job.actual_end)}` : ""}
                </p>
            )}
        </div>
    );
}

function ExpenseSection({ job, reload }) {
    const [form, setForm] = useState({
        description: "",
        amount: "",
        category: "materials",
        receipt_url: "",
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const add = async (e) => {
        e.preventDefault();
        if (!form.description.trim() || !form.amount) return;
        setSaving(true);
        setErr("");
        try {
            await api.post(`/jobs/${job.id}/expenses`, {
                description: form.description.trim(),
                amount: Number(form.amount),
                category: form.category,
                receipt_url: form.receipt_url.trim(),
            });
            setForm({ description: "", amount: "", category: "materials", receipt_url: "" });
            await reload();
        } catch (ex) {
            setErr(apiErrorMessage(ex));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (xid) => {
        try {
            await api.delete(`/jobs/${job.id}/expenses/${xid}`);
            await reload();
        } catch (ex) {
            setErr(apiErrorMessage(ex));
        }
    };

    return (
        <div className="brand-card fade-up delay-1" data-testid="jd-expenses">
            <div className="flex items-baseline justify-between">
                <p className="eyebrow">Expenses</p>
                <p className="text-sm text-muted">
                    Total <span className="font-mono">{money(job.total_expenses)}</span>
                </p>
            </div>

            <form
                onSubmit={add}
                className="mt-4 grid grid-cols-12 gap-2 items-end"
                data-testid="jd-expense-form"
            >
                <div className="col-span-12 md:col-span-5">
                    <label className="field-label">Description</label>
                    <input
                        className="field-input"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Paint, supplies, gas…"
                        required
                        data-testid="jd-expense-desc"
                    />
                </div>
                <div className="col-span-4 md:col-span-2">
                    <label className="field-label">Amount</label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="field-input"
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        required
                        data-testid="jd-expense-amount"
                    />
                </div>
                <div className="col-span-8 md:col-span-3">
                    <label className="field-label">Category</label>
                    <select
                        className="field-select"
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        data-testid="jd-expense-category"
                    >
                        {EXPENSE_CATEGORIES.map((c) => (
                            <option key={c} value={c} className="capitalize">
                                {c}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-span-12 md:col-span-2">
                    <button
                        type="submit"
                        className="btn btn-primary w-full"
                        disabled={saving}
                        data-testid="jd-expense-submit"
                    >
                        <Plus size={14} />
                        Add
                    </button>
                </div>
                <div className="col-span-12">
                    <input
                        className="field-input"
                        value={form.receipt_url}
                        onChange={(e) => setForm({ ...form, receipt_url: e.target.value })}
                        placeholder="Receipt URL (optional) — paste a link to a photo or PDF"
                        data-testid="jd-expense-receipt"
                    />
                </div>
            </form>
            {err && <p className="field-error mt-2">{err}</p>}

            <div className="mt-5">
                {(!job.expenses || job.expenses.length === 0) && (
                    <p className="text-muted text-sm" data-testid="jd-expense-empty">
                        No expenses logged yet.
                    </p>
                )}
                {job.expenses?.length > 0 && (
                    <ul className="flex flex-col gap-2">
                        {job.expenses.map((e) => (
                            <li
                                key={e.id}
                                className="flex items-center justify-between gap-3 p-3 rounded-md"
                                style={{ background: "var(--brand-cream)" }}
                                data-testid={`jd-expense-${e.id}`}
                            >
                                <div className="min-w-0">
                                    <p className="font-display font-bold">{e.description}</p>
                                    <p className="text-xs text-muted">
                                        {e.category} · {fmtDate(e.date)}
                                        {e.entered_by_name ? ` · ${e.entered_by_name}` : ""}
                                    </p>
                                    {e.receipt_url && (
                                        <a
                                            href={e.receipt_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="link-red text-xs"
                                        >
                                            View receipt
                                        </a>
                                    )}
                                </div>
                                <span className="font-mono font-bold">{money(e.amount)}</span>
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => remove(e.id)}
                                    aria-label="Remove"
                                    data-testid={`jd-expense-remove-${e.id}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function ChangeOrderSection({ job, reload }) {
    const [form, setForm] = useState({ description: "", amount: "", approved: true });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const add = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErr("");
        try {
            await api.post(`/jobs/${job.id}/change-order`, {
                description: form.description.trim(),
                amount: Number(form.amount),
                approved: form.approved,
            });
            setForm({ description: "", amount: "", approved: true });
            await reload();
        } catch (ex) {
            setErr(apiErrorMessage(ex));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="brand-card fade-up delay-2" data-testid="jd-change-orders">
            <div className="flex items-baseline justify-between">
                <p className="eyebrow">Change orders</p>
                <p className="text-sm text-muted">
                    Approved <span className="font-mono">{money(job.change_orders_total)}</span>
                </p>
            </div>

            <form
                onSubmit={add}
                className="mt-4 grid grid-cols-12 gap-2 items-end"
                data-testid="jd-co-form"
            >
                <div className="col-span-12 md:col-span-6">
                    <label className="field-label">Description</label>
                    <input
                        className="field-input"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        required
                        placeholder="Extra prep, additional door, etc."
                        data-testid="jd-co-desc"
                    />
                </div>
                <div className="col-span-6 md:col-span-3">
                    <label className="field-label">Amount</label>
                    <input
                        type="number"
                        step="0.01"
                        className="field-input"
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        required
                        data-testid="jd-co-amount"
                    />
                </div>
                <div className="col-span-6 md:col-span-2 flex items-center gap-2 pb-2">
                    <input
                        type="checkbox"
                        id="co-approved"
                        checked={form.approved}
                        onChange={(e) => setForm({ ...form, approved: e.target.checked })}
                        data-testid="jd-co-approved"
                    />
                    <label htmlFor="co-approved" className="text-sm">
                        Approved
                    </label>
                </div>
                <div className="col-span-12 md:col-span-1">
                    <button
                        type="submit"
                        className="btn btn-primary w-full"
                        disabled={saving}
                        data-testid="jd-co-submit"
                    >
                        <Plus size={14} />
                    </button>
                </div>
            </form>
            {err && <p className="field-error mt-2">{err}</p>}

            <div className="mt-5">
                {(!job.change_orders || job.change_orders.length === 0) && (
                    <p className="text-muted text-sm" data-testid="jd-co-empty">
                        No change orders yet.
                    </p>
                )}
                {job.change_orders?.length > 0 && (
                    <ul className="flex flex-col gap-2">
                        {job.change_orders.map((c) => (
                            <li
                                key={c.id}
                                className="flex items-center justify-between gap-3 p-3 rounded-md"
                                style={{ background: "var(--brand-cream)" }}
                                data-testid={`jd-co-${c.id}`}
                            >
                                <div className="min-w-0">
                                    <p className="font-display font-bold">{c.description}</p>
                                    <p className="text-xs text-muted">{fmtDate(c.date)}</p>
                                </div>
                                <span
                                    className={`badge-pill ${c.approved ? "badge-success" : "badge-muted"}`}
                                >
                                    {c.approved ? "approved" : "pending"}
                                </span>
                                <span className="font-mono font-bold">{money(c.amount)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
